'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { NumericFormat } from 'react-number-format';
import { InputContainer } from '../InputContainer';

type InputType = 'Cap Rate' | 'Price' | 'NOI';

export const CapitalRateCalculator: React.FC<{ inputType?: InputType }> = ({
  inputType: initialInputType = 'Cap Rate',
}) => {
  const [inputType, setInputType] = React.useState<InputType>(initialInputType);
  const [purchasePrice, setPurchasePrice] = React.useState(1000000);
  const [netOperatingIncome, setNetOperatingIncome] = React.useState(80000);
  const [capRate, setCapRate] = React.useState(8);
  const [grossRevenue, setGrossRevenue] = React.useState(0);
  const [expenses, setExpenses] = React.useState(0);

  const isInputTypeCapRate = inputType === 'Cap Rate';
  const isInputTypePrice = inputType === 'Price';
  const isInputTypeNOI = inputType === 'NOI';

  const allValues = React.useMemo(() => {
    return { capRate, expenses, grossRevenue, netOperatingIncome, purchasePrice };
  }, [capRate, expenses, grossRevenue, netOperatingIncome, purchasePrice]);

  const handleChangeType = (value: InputType) => setInputType(value);

  const calculate = React.useCallback(
    (newValues: {
      netOperatingIncome: number;
      purchasePrice: number;
      capRate: number;
      grossRevenue: number;
      expenses: number;
    }): void => {
      const newCapRate = (newValues.netOperatingIncome / newValues.purchasePrice) * 100;
      const newPurchasePrice = newValues.netOperatingIncome / newValues.capRate;
      const newNetOperatingIncome = newValues.grossRevenue - newValues.expenses;

      if (isInputTypeCapRate) {
        setCapRate(newCapRate);
      } else if (isInputTypePrice) {
        setPurchasePrice(newPurchasePrice);
      } else if (isInputTypeNOI) {
        setNetOperatingIncome(newNetOperatingIncome);
        setCapRate(newCapRate);
      }
    },
    [isInputTypeCapRate, isInputTypeNOI, isInputTypePrice],
  );

  const handleOnChangeNetOperatingIncome = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, netOperatingIncome: value });
      setNetOperatingIncome(value);
    },
    [allValues, calculate],
  );

  const handleOnChangePurchasePrice = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, purchasePrice: value });
      setPurchasePrice(value);
    },
    [allValues, calculate],
  );

  const handleOnChangeCapRate = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, capRate: value / 100 });
      setCapRate(value);
    },
    [allValues, calculate],
  );

  const handleOnChangeGrossRevenue = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, grossRevenue: value });
      setGrossRevenue(value);
    },
    [allValues, calculate],
  );

  const handleOnChangeExpenses = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, expenses: value });
      setExpenses(value);
    },
    [allValues, calculate],
  );

  return (
    <CapitalRateCalculatorContainer className="capital-rate-calculator-container">
      <div className="input-type-container">
        <button className={`${isInputTypeCapRate ? 'active' : ''}`} onClick={() => handleChangeType('Cap Rate')}>
          Cap Rate
        </button>
        <button className={`${isInputTypePrice ? 'active' : ''}`} onClick={() => handleChangeType('Price')}>
          Price
        </button>
        <button className={`${isInputTypeNOI ? 'active' : ''}`} onClick={() => handleChangeType('NOI')}>
          NOI
        </button>
      </div>

      <div className="inputs-container">
        {isInputTypePrice ? (
          <InputContainer name="Cap Rate" onChange={handleOnChangeCapRate} suffix="%" value={capRate} />
        ) : (
          <InputContainer
            name="Purchase Price"
            onChange={handleOnChangePurchasePrice}
            prefix="$"
            value={purchasePrice}
          />
        )}

        {isInputTypeNOI ? (
          <InputContainer name="Gross Revenue" onChange={handleOnChangeGrossRevenue} prefix="$" value={grossRevenue} />
        ) : (
          <InputContainer
            name="Net Operating Income"
            onChange={handleOnChangeNetOperatingIncome}
            prefix="$"
            value={netOperatingIncome}
          />
        )}
        {isInputTypeNOI && (
          <InputContainer name="Expenses" onChange={handleOnChangeExpenses} prefix="$" value={expenses} />
        )}
      </div>
      <div className="result-container">
        {isInputTypeCapRate ? (
          <>
            <span className="label">Cap Rate:</span>
            <NumericFormat
              decimalScale={2}
              displayType="text"
              fixedDecimalScale={true}
              suffix={'%'}
              thousandSeparator={true}
              value={capRate ? capRate : '-'}
            />
          </>
        ) : isInputTypeNOI ? (
          <>
            <div>
              <span className="label">Net Operating Income:</span>
              <NumericFormat
                decimalScale={2}
                displayType="text"
                fixedDecimalScale={true}
                prefix={'$'}
                thousandSeparator={true}
                value={netOperatingIncome ? netOperatingIncome : '-'}
              />
            </div>
            <div>
              <span className="label">Cap Rate:</span>
              <NumericFormat
                decimalScale={2}
                displayType="text"
                fixedDecimalScale={true}
                suffix={'%'}
                thousandSeparator={true}
                value={capRate ? capRate : '-'}
              />
            </div>
          </>
        ) : (
          <>
            <span className="label">Purchase Price:</span>
            <NumericFormat
              decimalScale={2}
              displayType="text"
              fixedDecimalScale={true}
              prefix={'$'}
              thousandSeparator={true}
              value={purchasePrice ? purchasePrice : '-'}
            />
          </>
        )}
      </div>
    </CapitalRateCalculatorContainer>
  );
};

const CapitalRateCalculatorContainer = styled.div`
  &.capital-rate-calculator-container {
    display: flex;
    flex-direction: column;
    align-items: center;

    .input-type-container {
      width: 100%;
      height: 40px;
      display: flex;
      margin-bottom: 20px;
      margin-right: auto;
      margin-left: auto;
      border-radius: ${({ theme }) => theme.borderRadius.default};
      overflow: hidden;
      box-sizing: border-box;

      button {
        width: 100%;
        height: 100%;
        color: ${({ theme }) => theme.colors.brand};
        background: ${({ theme }) => theme.colorPalette.white};
        overflow: hidden;
        border: 2px solid ${({ theme }) => theme.colors.border};
        border-radius: ${({ theme }) => theme.borderRadius.default};
        font-weight: ${({ theme }) => theme.fontWeight.semibold};

        &:nth-of-type(1) {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-right-width: 0;
        }

        &:nth-of-type(2) {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        &:nth-of-type(3) {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-left-width: 0;
        }

        &.active {
          color: ${({ theme }) => theme.colorPalette.white};
          background: ${({ theme }) => theme.colors.brand};
        }
      }
    }
  }

  .inputs-container {
    width: 100%;

    .input-container {
      margin-bottom: 10px;
      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  .result-container {
    color: ${({ theme }) => theme.colorPalette.gray500};
    font-size: ${({ theme }) => theme.fontSize.lg};
    margin-top: 20px;
    text-align: center;

    > div {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
    }

    .label {
      margin-right: 10px;
    }
  }
`;
